import currency from "currency.js";

const moneda = (value: number | string) =>
  currency(value, {
    symbol: "$",
    decimal: ",",
    separator: ".",
    precision: 2,
    pattern: `! #`,
    // Sin esto queda el default `-!#`, así que los negativos salían "-$1.000,00"
    // (sin el espacio), inconsistente con el patrón positivo "! #".
    negativePattern: `-! #`,
  });

export function formatCurrency(amount: string | number) {
  const parsed = moneda(amount);

  // `NaN` no es un precio. currency.js lo renderizaba como "-$NaN" (elige el
  // patrón negativo porque `NaN >= 0` es false). Devolvemos un placeholder en
  // vez de "$ 0,00": un cero inventado se lee como un importe válido y esconde
  // el error; "$ -" deja claro que no hay valor.
  if (!Number.isFinite(parsed.value)) {
    return "$ -";
  }

  return parsed.format();
}

/**
 * Formatea un importe que el backend manda como **string decimal** ("25000.00").
 *
 * # ⚠️ Por qué NO se le puede pasar ese string a `formatCurrency`
 *
 * `formatCurrency` está configurado a la argentina —`decimal: ","`,
 * `separator: "."`— así que al parsear un STRING lee el punto como **separador
 * de miles**. Medido:
 *
 * | valor del backend | `formatCurrency(string)` | lo correcto    |
 * | ----------------- | ------------------------ | -------------- |
 * | `"25000.00"`      | **$ 2.500.000,00**       | $ 25.000,00    |
 * | `"1234.56"`       | **$ 123.456,00**         | $ 1.234,56     |
 * | `"100.50"`        | **$ 10.050,00**          | $ 100,50       |
 *
 * O sea: **todo importe con decimales sale cien veces más grande**. Y no se ve
 * en una prueba rápida, porque un valor redondo (`"100"`) o un cero (`"0.00"`)
 * dan bien por casualidad. Es el mismo bicho que el `parsePercentage` duplicado
 * que este repo ya pagó, con el punto tratado como separador de miles.
 *
 * Se encontró el 2026-09-12 armando la pantalla de suscripción, cuyos montos
 * vienen así de octopus (trd-octopus#294: "son strings decimales: plata, no
 * float").
 *
 * # Qué hace
 *
 * Convierte con `Number()` —que sí lee el punto como decimal— y recién ahí
 * formatea. Un valor que no es número devuelve el mismo placeholder que
 * `formatCurrency`.
 */
export function formatDecimalString(
  amount: string | number | null | undefined
): string {
  if (amount === null || amount === undefined || amount === "") return "$ -";
  return formatCurrency(Number(amount));
}
